export type ErrorPageContent = {
	status: number;
	eyebrow: string;
	headline: string;
	body: string;
	documentTitle: string;
	description: string;
};

/** Never index error / access-denied shells (search + AI crawlers). */
export const ERROR_ROBOTS = 'noindex, nofollow, noarchive, nosnippet, noimageindex' as const;

export function getErrorPageContent(status: number): ErrorPageContent {
	if (status === 404) {
		return {
			status,
			eyebrow: 'Page not found',
			headline: 'This path left for safer ground.',
			body: 'We couldn’t find that page—but the mission continues. Head home, or support the next generation of Ukrainians.',
			documentTitle: `404 | Brave Generation`,
			description: 'Page not found. Return to Brave Generation or support Ukrainian youth.'
		};
	}

	if (status === 403) {
		return {
			status,
			eyebrow: 'Access closed',
			headline: 'This document isn’t open that way.',
			body: 'Financial reports open only through the viewer on our site—not from a copied link or a new tab. Head to Financials to view them securely.',
			documentTitle: `403 | Brave Generation`,
			description: 'Direct document access is not allowed. Open reports from the Financials page.'
		};
	}

	if (status === 405) {
		return {
			status,
			eyebrow: 'Wrong door',
			headline: 'That address isn’t meant for browsing.',
			body: 'This endpoint only works behind the scenes. There’s nothing to open here—return home or visit Financials for the reports.',
			documentTitle: `405 | Brave Generation`,
			description: 'This method is not allowed. Return to Brave Generation.'
		};
	}

	if (status >= 500) {
		return {
			status,
			eyebrow: 'Server error',
			headline: 'Something stumbled on our side.',
			body: 'We’re already looking into it. Please try again in a moment—your support still reaches the people who need it.',
			documentTitle: `${status} | Brave Generation`,
			description: 'A temporary server error occurred. Please try again shortly.'
		};
	}

	if (status >= 400) {
		return {
			status,
			eyebrow: 'Request issue',
			headline: 'That request didn’t land.',
			body: 'Something about this request didn’t work. Double-check the address, or continue from the homepage.',
			documentTitle: `${status} | Brave Generation`,
			description: 'The request could not be completed. Return to Brave Generation.'
		};
	}

	return {
		status,
		eyebrow: 'Unexpected error',
		headline: 'We hit an unexpected turn.',
		body: 'Something went wrong. Please try again, or return home to keep exploring the mission.',
		documentTitle: `Error | Brave Generation`,
		description: 'An unexpected error occurred. Return to Brave Generation.'
	};
}

export function isPrivateErrorPath(pathname: string): boolean {
	return pathname === '/errors' || pathname.startsWith('/errors/');
}
